import type { ISender } from "@/types";
import { accountVariables } from "./account-variables";

type SenderUser =
  | {
      firstName?: string | null;
      email?: string | null;
      profile?: unknown;
    }
  | null
  | undefined;

// Every built-in `sender_<field>` other than name/email is stored at
// `user.profile.<field>` — derived from the one list (account-variables.ts)
// so a sender variable added there is filled everywhere without another edit.
const PROFILE_KEY_BY_NAME = new Map(
  accountVariables
    .map((v) => v.variable.name)
    .filter(
      (name) =>
        name.startsWith("sender_") &&
        name !== "sender_name" &&
        name !== "sender_email",
    )
    .map((name) => [name, name.slice("sender_".length)] as const),
);

/** `sender_role` → `role`: the `user.profile` key a built-in sender variable
 * is stored under, or null (name / email live on the user; anything else is
 * not a built-in). */
export const senderProfileKey = (name: string): string | null =>
  PROFILE_KEY_BY_NAME.get(name) ?? null;

/**
 * The sender half of IProfileData, built from the signed-in user: the
 * built-in sender fields plus the profile custom fields. The one place the
 * share dialog, fs-values, the integrations panel, the Variables page and the
 * rendered page's sender block (render-story, minus customFields) read sender
 * values from.
 */
export const buildSenderData = (user: SenderUser): ISender => {
  const profile = (user?.profile ?? {}) as Record<string, unknown>;
  const sender: Record<string, unknown> = {
    name: user?.firstName ?? "",
    email: user?.email ?? "",
  };
  const customFields = (
    profile.customFields && typeof profile.customFields === "object"
      ? profile.customFields
      : {}
  ) as Record<string, unknown>;
  for (const field of Array.from(PROFILE_KEY_BY_NAME.values())) {
    const value = profile[field];
    // A built-in that used to be creatable as a profile custom field
    // (sender_linkedin, sender_location, sender_address) keeps that value
    // until the sender fills the built-in one.
    const legacy = customFields[`sender_${field}`];
    sender[field] =
      typeof value === "string" && value
        ? value
        : typeof legacy === "string"
          ? legacy
          : "";
  }
  sender.customFields = customFields;
  return sender as unknown as ISender;
};
